/**
 * Initializes an Alpine.js data object to manage the addition of products to the cart, including cross-sell options.
 * Key features include:
 * 1. Cart Integration: Adds selected product variants to the cart with optional cross-sell products.
 * 2. Error Handling: Manages responses from the cart addition process, providing user feedback on failure.
 * 3. Sticky Button Visibility: Utilizes an IntersectionObserver to toggle the visibility of a sticky add-to-cart button based on scroll position.
 * 4. Dynamic UI Adjustments: Adapts the cart drawer's behavior based on screen width and user interactions.
 */

const heroProductAdd = () => ({
  drawerSide: '',
  stickyButtonVisible: false,

  get isSoldOut() {
    return !this.finalSelectedVariant?.availableForSale;
  },

  addToCart: async function () {
    const itemsToAdd = [{ id: this.finalSelectedVariant.id }];

    if (this.crossSellChecked && this.crossSellVariant.id) {
      itemsToAdd.push({ id: this.crossSellVariant.id });
    }

    try {
      this.isWorking = true;
      const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'post',
        body: JSON.stringify({ items: itemsToAdd }),
        headers: { 'content-type': 'application/json' },
      });

      if (!res.ok) {
        let description = '';
        try {
          const data = await res.json();
          description = data?.description;
        } catch (e) {
          //
        }
        throw new Error([`Failed to add to cart!`, description, `(status: ${res.status})`].filter(Boolean).join('\n'));
      }

      this.$dispatch('update-and-open-cart', {
        noOpenAfterUpdate: window.CURRENT_TEMPLATE === 'cart',
      });
    } catch (error) {
      alert(error.message);
      this.isWorking = false;
      throw error;
    }
  },

  init() {
    this.setupStickyButton();
  },

  destroy() {
    this.intersectionObserver?.disconnect();
  },

  async handleButtonClick() {
    const { finalSelectedVariant } = this;
    if (finalSelectedVariant.availableForSale) {
      await this.addToCart();
      this.isWorking = false;
    } else {
      this.$dispatch('open-quick-add-drawer', {
        product: this.currentProduct,
        selectedOptions: this.selectedOptions,
      });
    }
  },

  addBundleToCart: async function () {
    const button = document.getElementById('atc-bundle');

    if (!button) return;

    const variant1 = button.dataset.var1;
    const variant2 = button.dataset.var2;

    if (!variant1 || !variant2) return;

    try {
      this.isWorking = true;

      const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variant1),
              quantity: 1
            },
            {
              id: Number(variant2),
              quantity: 1
            }
          ]
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.description || 'Failed to add bundle to cart.');
      }

      this.$dispatch('update-and-open-cart', {
        noOpenAfterUpdate: window.CURRENT_TEMPLATE === 'cart',
      });

    } catch (error) {
      alert(error.message);
      throw error;

    } finally {
      this.isWorking = false;
    }
  },

  addBundleToCart2: async function (event) {
    const button = event.currentTarget;

    if (!button) return;

    const variant1 = button.dataset.var1;
    const variant2 = button.dataset.var2;

    if (!variant1 || !variant2) return;

    try {
      this.isWorking = true;

      // Main bundle items
      const items = [
        {
          id: Number(variant1),
          quantity: 1
        },
        {
          id: Number(variant2),
          quantity: 1
        }
      ];

      // Accessories Bundle upsell
      const container = button.closest('.hero-product-add');
      const crossSell = container
        ? container.querySelector('#crossSell')
        : null;

      if (crossSell && crossSell.checked) {
        const crossSellVariantId = crossSell.dataset.id;

        if (crossSellVariantId) {
          items.push({
            id: Number(crossSellVariantId),
            quantity: 1
          });
        }
      }

      const res = await fetch(
        `${window.Shopify.routes.root}cart/add.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items: items
          })
        }
      );

      if (!res.ok) {
        const data = await res.json();

        throw new Error(
          data.description ||
          'Failed to add bundle to cart.'
        );
      }

      this.$dispatch('update-and-open-cart', {
        noOpenAfterUpdate:
          window.CURRENT_TEMPLATE === 'cart',
      });

    } catch (error) {
      alert(error.message);
      throw error;

    } finally {
      this.isWorking = false;
    }
  },

  handleDrawerOpen(e) {
    const matcher = window.matchMedia(`(min-width: 1024px)`);
    this.drawerSide = matcher.matches ? 'right' : 'bottom';

    e.target.closest('button').parentElement.querySelector('dialog').showModal();
  },

  setupStickyButton() {
    this.intersectionObserver = new IntersectionObserver(([entry]) => {
      this.stickyButtonVisible = !entry.isIntersecting;
    }, {});

    this.intersectionObserver.observe(this.$refs.button);
  },
});

document.addEventListener('alpine:init', () => {
  Alpine.data('heroProductAdd', heroProductAdd);
});